import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import getAPIbaseURL from "../../../utils/helpers";
import { Container, Row, Col, Button, Form, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaArrowRight, FaFilter, FaRegPaperPlane } from 'react-icons/fa';
import { FiTrash } from "react-icons/fi";
import { fetchNotifications, deleteNotification } from '../../../redux/slices/notificationsSlice';
import { fetchUsers } from '../../../redux/slices/userSlice';
import useSocketConnect from '../../../hooks/useSocket';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, formatTime } from '../../../utils/timeFormat';

const NotificationCenter = () => {
    const dispatch = useDispatch();
    const notifications = useSelector((state) => state.notifications.items);
    const users = useSelector((state) => state.users.items);
    const user = useSelector((state) => state.auth.user);
    const error = useSelector((state) => state.notifications.error);
    const navigate = useNavigate();

    const socket = useSocketConnect();

    useEffect(() => {
        if (!users || users.length === 0) {
            dispatch(fetchUsers());
        }
    }, [dispatch, users]);

    useEffect(() => {
        if (user?.id) {
            dispatch(fetchNotifications({ author_id: user.id }));
        }
    }, [dispatch, user]);

    useEffect(() => {
        if (!socket || !user?.id) return;

        const eventName = `user_${user.id}`;
        const handleNotification = (data) => {
            console.log("Received notification via socket:", data);
            dispatch(fetchNotifications({ author_id: user.id }));
        };

        socket.on(eventName, handleNotification);

        return () => {
            socket.off(eventName, handleNotification);
        };
    }, [socket, user, dispatch]);

    useEffect(() => {
        if (error) {
            console.error("Error fetching notifications:", error);
        }
    }, [error]);

    const handleDeleteNotification = (id) => {
        dispatch(deleteNotification(id));
    };

    const handleClearNotifications = () => {
        notifications.forEach(n => dispatch(deleteNotification(n.id)));
    };

    const handleArrowClick = () => {
        navigate('/backoffice?tab=absences');
    };

    const styles = {
        container: {
            backgroundColor: '#1E0342',
            minHeight: '100vh',
            padding: '20px',
        },
        searchInput: {
            backgroundColor: '#fff',
            color: '#000',
            border: 'none',
            borderRadius: '10px',
            width: '200px',
            marginRight: '10px',
        },
        filterButton: {
            backgroundColor: '#0E46A3',
            border: 'none',
            borderRadius: '10px',
            padding: '10px',
        },
        card: {
            backgroundColor: 'transparent',
            border: 'none',
            color: 'white',
            borderRadius: '0px',
        },
        userImage: {
            height: '90px',
            width: '90px',
            objectFit: 'cover',
            borderRadius: '50%',
        },
        rejectButton: {
            backgroundColor: '#7e0000',
            color: '#fff',
            border: 'none',
            padding: '5px 15px',
            borderRadius: '5px',
            width: '115px',
        },
        acceptButton: {
            backgroundColor: '#0e8104',
            color: '#fff',
            border: 'none',
            padding: '5px 15px',
            borderRadius: '5px',
            width: '115px',
        },
        newBadge: {
            backgroundColor: '#1E0342',
            color: '#00B2FF',
            border: '1px solid #00B2FF',
            borderRadius: '7px',
            fontSize: '0.8rem',
            padding: '5px 20px',
        },
        arrowButton: {
            backgroundColor: '#17296B',
            border: 'none',
            padding: '10px',
            boxShadow: '0 0 5px rgba(0, 0, 0, 1.2)',
        },
        trashButton: {
            backgroundColor: '#ff4d4d',
            border: 'none',
            padding: '10px',
            boxShadow: '0 0 5px rgba(0, 0, 0, 1.2)',
        },
    };

    const renderNotificationField = (field) => {
        if (field === null || field === undefined) return "";
        if (typeof field === "object") {
            try {
                return JSON.stringify(field);
            } catch {
                return String(field);
            }
        }
        return field;
    };

    return (
        <Container fluid style={styles.container}>
            <Row className="mb-2">
                <Col xs={6} className="d-flex align-items-center">
                    <h2 className="text-white fw-bold">Central de Notificações</h2>
                </Col>
                <Col xs={6} className="d-flex justify-content-end align-items-center">
                    <Form.Control
                        type="text"
                        placeholder="Pesquise..."
                        style={styles.searchInput}
                    />
                    <Button variant="primary" style={styles.filterButton}>
                        <FaFilter />
                    </Button>
                </Col>
            </Row>
            <Row className="mb-4">
                <Col xs={12} className="d-flex justify-content-start">
                    <Button
                        variant="danger"
                        onClick={handleClearNotifications}
                        style={styles.filterButton}
                    >
                        Limpar Notificações
                    </Button>
                </Col>
            </Row>
            <Row>
                {notifications.map((notification) => (
                    <div key={notification.id} style={{ borderBottom: "2px solid white" }}>
                        <Card className="mb-2" style={styles.card}>
                            <Card.Body>
                                <Row>
                                    <Col xs={1} className="d-flex align-items-center">
                                        <img
                                            src={(() => {
                                                const user = users.find(u => u.id === notification.author_id);
                                                if (user && user.profile_picture) {
                                                    if (user.profile_picture.startsWith("http")) {
                                                        return user.profile_picture;
                                                    } else {
                                                        return `${getAPIbaseURL()}${user.profile_picture}`;
                                                    }
                                                }
                                                return notification.author && notification.author.profile_picture ? notification.author.profile_picture : 'https://imgs.search.brave.com/YfyNSZIduSszrOd2DIfVpcEZXVPxARydF3-FOuI_1pA/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly93d3cu/dzNzY2hvb2xzLmNv/bS9ob3d0by9pbWdf/YXZhdGFyLnBuZw';
                                            })()}
                                            alt={(() => {
                                                const user = users.find(u => u.id === notification.author_id);
                                                return user ? user.username : (notification.author ? notification.author.username : "User");
                                            })()}
                                            className="rounded-circle img-fluid"
                                            style={styles.userImage}
                                        />
                                    </Col>
                                    <Col xs={8}>
                                        <p className="mb-2">{renderNotificationField(notification.title) || renderNotificationField(notification.message)}</p>
                                        <p>{renderNotificationField(notification.content)}</p>
                                    </Col>
                                    <Col xs={3} className="d-flex justify-content-end align-items-center">
                                        <div className="d-flex flex-column align-items-end me-4 mt-2">
                                            {notification.isNew && (
                                                <p style={styles.newBadge}>New</p>
                                            )}
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip id={`tooltip-${notification.id}`}>
                                                        {notification.created_at
                                                            ? formatDateTime(notification.created_at)
                                                            : ""}
                                                    </Tooltip>
                                                }
                                            >
                                                <small className="text-white">
                                                    <FaRegPaperPlane size={18} className="me-2" />
                                                    {notification.created_at
                                                        ? formatTime(notification.created_at)
                                                        : ""}
                                                </small>
                                            </OverlayTrigger>
                                        </div>
                                        <div className="d-flex">
                                            <Button
                                                variant="primary"
                                                className="me-3"
                                                style={styles.arrowButton}
                                                onClick={handleArrowClick}
                                            >
                                                <FaArrowRight size={25} />
                                            </Button>
                                            <Button
                                                variant="danger"
                                                style={styles.trashButton}
                                                onClick={() => handleDeleteNotification(notification.id)}
                                            >
                                                <FiTrash size={25} />
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                ))}
            </Row>
        </Container>
    );
};

export default NotificationCenter;
